import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Filter, FolderOpen, ChevronRight, DollarSign, AlertTriangle, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { ProjectCard } from '@/components/dashboard/ProjectCard';
import { DashboardAnalytics } from '@/components/dashboard/DashboardAnalytics';
import { NewProjectDialog } from '@/components/dashboard/NewProjectDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewProject, setShowNewProject] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dashAnalytics, setDashAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [savingsTotal, setSavingsTotal] = useState(null);
  const [materialsHealth, setMaterialsHealth] = useState({});
  const [priceSavings, setPriceSavings] = useState(null);

  const [newProject, setNewProject] = useState({
    name: '',
    client_name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    project_type: 'Other',
    start_date: '',
    end_date: '',
    total_budget: '',
    status: 'Active'
  });

  const [refreshing, setRefreshing] = useState(false);
  const pullStartY = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchProjects();

    // Best-effort: fetch product savings total for the dashboard pill
    axios.get(`${API}/analytics/products/savings`, { withCredentials: true })
      .then((r) => setSavingsTotal(r.data?.total_potential_savings ?? 0))
      .catch(() => setSavingsTotal(null));

    // Best-effort: materials-health for the per-project badge
    axios.get(`${API}/projects/materials-health`, { withCredentials: true })
      .then((r) => setMaterialsHealth(r.data || {}))
      .catch(() => setMaterialsHealth({}));

    // Best-effort: price-check savings identified this month
    axios.get(`${API}/price-intel/savings-summary`, { withCredentials: true })
      .then((r) => setPriceSavings(r.data))
      .catch(() => setPriceSavings(null));

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pull-to-refresh for mobile
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e) => { if (el.scrollTop === 0) pullStartY.current = e.touches[0].clientY; };
    const onTouchEnd = async (e) => {
      if (pullStartY.current !== null) {
        const pullDist = e.changedTouches[0].clientY - pullStartY.current;
        if (pullDist > 80 && el.scrollTop === 0) {
          setRefreshing(true);
          await fetchProjects();
          setRefreshing(false);
        }
      }
      pullStartY.current = null;
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => { el.removeEventListener('touchstart', onTouchStart); el.removeEventListener('touchend', onTouchEnd); };
  }, []);

  const fetchProjects = async () => {
    try {
      const [projectsRes, teamRes, analyticsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/projects/`, { withCredentials: true }),
        axios.get(`${API}/settings/team`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/alerts/`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setProjects(projectsRes.data);
      setTeamMembers(teamRes.data.filter(m => m.status === 'Active'));
      if (analyticsRes.data) setDashAnalytics(analyticsRes.data);
      setAlerts(alertsRes.data.filter(a => !a.read && (a.severity === 'Critical' || a.severity === 'Warning')).slice(0, 3));
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to load projects');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTeamMember = (userId) => {
    return teamMembers.find(m => m.user_id === userId);
  };

  const handleNewProjectClick = () => {
    if (isMobile) {
      navigate('/projects/new');
    } else {
      setShowNewProject(true);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);

    // Combine address fields
    const addressParts = [newProject.street, newProject.city, newProject.state, newProject.zip].filter(Boolean);
    const address = addressParts.join(', ');

    try {
      const response = await axios.post(`${API}/projects/`, {
        name: newProject.name,
        client_name: newProject.client_name,
        address: address || null,
        project_type: newProject.project_type,
        start_date: newProject.start_date || null,
        end_date: newProject.end_date || null,
        total_budget: parseFloat(newProject.total_budget) || 0,
        status: newProject.status
      }, {
        withCredentials: true
      });
      
      setShowNewProject(false);
      setNewProject({
        name: '',
        client_name: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        project_type: 'Other',
        start_date: '',
        end_date: '',
        total_budget: '',
        status: 'Active'
      });
      toast.success('Project created!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div ref={containerRef} className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Pull-to-refresh indicator */}
      {refreshing && (
        <div className="flex justify-center py-3 -mt-2 mb-2">
          <RefreshCw className="w-5 h-5 animate-spin text-primary" />
        </div>
      )}
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wide text-zinc-900 mb-1">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-zinc-500 text-sm">
            {projects.length} {projects.length === 1 ? 'project' : 'projects'} in your portfolio
          </p>
        </div>
        {savingsTotal !== null && savingsTotal > 0 && (
          <button
            onClick={() => {
              // Fire-and-forget telemetry — don't block navigation
              axios.post(`${API}/telemetry/events`, {
                event: 'savings_pill_clicked',
                payload: { amount: savingsTotal },
              }, { withCredentials: true }).catch(() => {});
              navigate('/analytics/products');
            }}
            data-testid="dashboard-savings-pill"
            className="savings-pill group inline-flex items-center gap-2 h-11 pl-3 pr-3 sm:pr-4 bg-primary border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:bg-primary-hover hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-150"
            title="See where you can save on materials"
          >
            <DollarSign className="w-4 h-4 text-zinc-900 shrink-0" />
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="savings-amount font-mono font-black text-sm sm:text-base text-zinc-900 whitespace-nowrap">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
                }).format(savingsTotal)}
              </span>
              <span className="text-[10px] sm:text-[11px] font-heading font-bold uppercase tracking-wider text-zinc-900/80 hidden sm:inline whitespace-nowrap">
                in savings available
              </span>
              <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-900/80 sm:hidden">
                savings
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-900 shrink-0 group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>

      {/* Critical/Warning Alerts Banner */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="dashboard-alerts">
          {alerts.map((alert) => (
            <button
              key={alert.alert_id}
              onClick={() => {
                if (alert.link_type === 'project' && alert.link_id) {
                  navigate(`/projects/${alert.link_id}`);
                }
              }}
              data-testid={`dashboard-alert-${alert.alert_id}`}
              className={`w-full flex items-start gap-3 p-3 sm:p-4 border text-left transition-all ${
                alert.severity === 'Critical'
                  ? 'bg-red-50 border-red-300'
                  : 'bg-amber-50 border-amber-300'
              }`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {alert.severity === 'Critical'
                  ? <AlertTriangle className="w-5 h-5 text-red-500" />
                  : <AlertCircle className="w-5 h-5 text-yellow-600" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{alert.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{alert.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-1" />
            </button>
          ))}
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
            className="w-full h-10 border border-zinc-200 bg-white text-zinc-900 text-sm pl-10 pr-4 focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter"
            className="h-10 border border-zinc-200 bg-white text-zinc-900 text-sm pl-10 pr-8 focus:outline-none focus:border-primary appearance-none"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="On Hold">On Hold</option>
            <option value="Complete">Complete</option>
          </select>
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid gap-4" data-testid="dashboard-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white border border-zinc-200 p-4 sm:p-6 animate-pulse">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="h-5 w-48 bg-zinc-100 mb-2" />
                  <div className="h-4 w-32 bg-zinc-100 mb-3" />
                </div>
                <div className="h-5 w-16 bg-zinc-100" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div><div className="h-3 w-16 bg-zinc-100 mb-1" /><div className="h-4 w-24 bg-zinc-100" /></div>
                <div><div className="h-3 w-16 bg-zinc-100 mb-1" /><div className="h-4 w-24 bg-zinc-100" /></div>
                <div><div className="h-3 w-16 bg-zinc-100 mb-1" /><div className="h-4 w-24 bg-zinc-100" /></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 sm:p-12 text-center" data-testid="empty-state">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
          <h2 className="font-heading text-xl font-bold uppercase tracking-wide text-zinc-900 mb-2">
            {projects.length === 0 ? 'No Projects Yet' : 'No Matching Projects'}
          </h2>
          <p className="text-zinc-500 text-sm mb-6 max-w-md mx-auto">
            {projects.length === 0 
              ? 'Create your first project to start tracking materials and budgets.'
              : 'Try adjusting your search or filter.'
            }
          </p>
          {projects.length === 0 && (
            <button
              onClick={handleNewProjectClick}
              data-testid="empty-new-project-btn"
              className="h-10 px-6 bg-primary text-zinc-900 font-heading font-bold uppercase tracking-wider text-xs hover:bg-primary-hover transition-colors"
            >
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.project_id}
              project={project}
              health={materialsHealth[project.project_id]}
              getTeamMember={getTeamMember}
              formatCurrency={formatCurrency}
            />
          ))}
        </div>
      )}

      {priceSavings && priceSavings.total_overpay > 0 && (
        <button
          onClick={() => navigate('/inbox')}
          data-testid="savings-found-tile"
          className="w-full mt-8 relative bg-[#141414] border-2 border-black shadow-[4px_4px_0_0_#111111] overflow-hidden text-left group hover:shadow-[2px_2px_0_0_#111111] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary" />
          <div className="pl-5 pr-4 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-primary font-heading font-semibold uppercase tracking-[0.2em] text-[10px] mb-1">
                Price Check &middot; Savings Found This Month
              </p>
              <p className="text-white font-mono font-bold text-2xl sm:text-3xl" data-testid="savings-found-amount">
                {formatCurrency(priceSavings.total_overpay)}
              </p>
              <p className="text-zinc-400 text-xs mt-0.5">
                identified across {priceSavings.invoices_flagged} of {priceSavings.invoices_analyzed} invoice{priceSavings.invoices_analyzed !== 1 ? 's' : ''} analyzed
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
      )}

      {dashAnalytics && projects.length > 0 && (
        <DashboardAnalytics dashAnalytics={dashAnalytics} formatCurrency={formatCurrency} />
      )}

      {/* Floating Action Button - Hidden for Foremen */}
      {user?.role !== 'Foreman' && (
        <button
          onClick={handleNewProjectClick}
          data-testid="fab-new-project"
          data-tour="new-project"
          className="fixed bottom-24 right-4 sm:bottom-8 sm:right-8 h-14 w-14 bg-primary flex items-center justify-center z-[100] hover:bg-primary-hover active:scale-95 transition-all fab-pulse"
        >
          <Plus className="w-8 h-8 text-zinc-900" />
        </button>
      )}

      <NewProjectDialog
        open={showNewProject}
        onOpenChange={setShowNewProject}
        newProject={newProject}
        setNewProject={setNewProject}
        creating={creating}
        onSubmit={handleCreateProject}
      />
    </div>
  );
};

export default DashboardPage;
